import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import AuthSubmitButton from 'src/features/auth/components/auth-submit-button'
import { sendEvent } from 'src/shared/api/events/send-event'
import { Form } from 'src/shared/components/ui/forms/form'
import AuthInputFormField from 'src/shared/features/auth/components/auth-input-form-field'
import type { LastNameFormData, OnboardingData } from 'src/shared/features/users/schemas/user'
import { lastNameFormSchema } from 'src/shared/features/users/schemas/user'
import { useEventStore } from 'src/shared/store/event-store'

type LastNameStepProps = {
  onNext: (data: LastNameFormData) => void
  initialData?: Partial<OnboardingData>
}

const LastNameStep = ({ onNext, initialData }: LastNameStepProps) => {
  const form = useForm<LastNameFormData>({
    resolver: zodResolver(lastNameFormSchema),
    defaultValues: {
      lastName: initialData?.lastName?.trim() || '',
    },
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const hasInitializedRef = useRef(false)

  // Access only what we need from event store
  const anonymousId = useEventStore((state) => state.anonymousId)
  const sessionId = useEventStore((state) => state.sessionId)
  const flowId = useEventStore((state) => state.flowId)
  const setAnonymousId = useEventStore((state) => state.setAnonymousId)
  const setSessionId = useEventStore((state) => state.setSessionId)
  const setFlowId = useEventStore((state) => state.setFlowId)

  // Watch for lastname changes and strip whitespace
  const lastName = form.watch('lastName')

  useEffect(() => {
    if (lastName && lastName.trim() !== lastName) {
      form.setValue('lastName', lastName.trim(), { shouldValidate: true })
    }
  }, [lastName, form])

  // Setup event IDs at component mount time, only once
  useEffect(() => {
    if (hasInitializedRef.current) return

    if (!anonymousId) {
      setAnonymousId()
    }
    if (!sessionId) {
      setSessionId()
    }
    if (!flowId) {
      setFlowId()
    }

    hasInitializedRef.current = true
  }, [anonymousId, sessionId, flowId, setAnonymousId, setSessionId, setFlowId])

  const onSubmit = async (data: LastNameFormData) => {
    if (isSubmitting) return

    setIsSubmitting(true)

    try {
      // Trim whitespace from inputs before submission
      const formattedData = {
        lastName: data.lastName.trim(),
      }

      const eventData = {
        firstName: initialData?.firstName?.trim() || '',
        lastName: data.lastName.trim(),
      }

      // Use the values from the hooks instead of directly accessing the store
      // This ensures we have the most up-to-date values from the component's perspective
      if (anonymousId && sessionId && flowId) {
        sendEvent({
          anonymousId,
          eventType: 'flow_start',
          info: {
            sessionId,
            flowType: 'onboarding',
            flowStep: 'submit_name',
            flowId,
            ...eventData,
          },
        })
        onNext(formattedData)
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <AuthInputFormField control={form.control} name="lastName" label="Last Name" />
        <AuthSubmitButton type="submit" isLoading={isSubmitting} className="mt-[25px] bg-primary">
          Continue
        </AuthSubmitButton>
      </form>
    </Form>
  )
}

LastNameStep.displayName = 'LastNameStep'

export default LastNameStep
